import { useCallback, useMemo } from 'react';
import { Tag } from '@nimbus-ds/components';
import { MoneyIcon } from '@nimbus-ds/icons';
import { MenuButton } from '@nimbus-ds/patterns';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Option } from '@tiendanube/common';
import { Country } from '@tiendanube/common/src/enums';
import { config } from 'App/components/ExternalServices/Amplitude/config';
import ShowByRole from 'App/components/ShowByRole';
import { useHandleMenuClick } from 'App/hooks';
import { useMenu } from 'App/settings/menu/hooks';
import { getIcon } from 'App/settings/menu/utils';
import { trackingMenuClick, trackingNuvempagoMenuClick } from 'App/tracking';
import { useGetCountry } from 'domains/Auth/hooks';
import { addOldAdminPrefix } from '../../../utils';

const getButtonElement = ({ oldAdmin }: Option) => (oldAdmin ? 'a' : Link);
const getOptionHref = ({ oldAdmin, goTo }: Option) =>
  oldAdmin ? addOldAdminPrefix(goTo ?? '') : undefined;
const clear = (str: string) => str.replace('#', '').replace(/\/$/, '');

function NuvemPagoMenu() {
  const { t } = useTranslation('common');
  const { pathname, hash } = useLocation();
  const { handleClickAccordion, handleClickItem } = useHandleMenuClick();
  const { menu } = useMenu();
  const storeCountry = useGetCountry();
  const isMexico = storeCountry === Country.MX;

  const isActive = useCallback(
    (goTo: string, useHash = true): boolean => {
      const [path, fragment] = goTo.split('#').map(clear);
      const isSamePath = clear(pathname) === path;

      if (!useHash) return isSamePath;

      return isSamePath && clear(hash).startsWith(fragment);
    },
    [pathname, hash],
  );

  const { NuvemPago: nuvemPagoMenu, PagoNube: pagoNubeMenu } =
    menu?.Main.FinancialServicesPayments?.options ?? {};

  // Pago Nube menu state and actions
  const pagoNubeMenuIsActive =
    !!pagoNubeMenu?.goTo && isActive(pagoNubeMenu.goTo, false);

  const handlePagoNubeClick = () => {
    trackingNuvempagoMenuClick(pagoNubeMenu ? 'active' : 'inactive');
    handleClickItem();
  };

  // Nuvem Pago menu state and actions
  const nuvemPagoMenuIsActive = useMemo(() => {
    const { goTo, options = {} } = nuvemPagoMenu ?? {};
    const parentIsActive = !!goTo && isActive(goTo, false);

    if (parentIsActive) return true;

    return Object.values(options).some(({ goTo }) => isActive(goTo ?? ''));
  }, [nuvemPagoMenu, isActive]);

  const handleNuvemPagoClick = useCallback(
    (option: Option, isParent = false) =>
      () => {
        if (option.tracking) {
          trackingMenuClick({
            ...option.tracking,
            instanceName: config.nuvempagoInstanceName,
          });
        }

        isParent ? handleClickAccordion() : handleClickItem();
      },
    [handleClickAccordion, handleClickItem],
  );

  return (
    <ShowByRole includeRoles="nuvem_pago">
      <>
        {pagoNubeMenu && (
          <MenuButton
            as={Link}
            to={pagoNubeMenu.goTo ?? ''}
            startIcon={MoneyIcon}
            onClick={handlePagoNubeClick}
            active={pagoNubeMenuIsActive}
            label={pagoNubeMenu.label}
          >
            {!!pagoNubeMenu.tag && !isMexico && (
              <Tag appearance="primary">{pagoNubeMenu.tag}</Tag>
            )}
            {isMexico && (
              <Tag appearance="primary">
                {t('menu.manage.nuvempago.newTag')}
              </Tag>
            )}
          </MenuButton>
        )}

        {nuvemPagoMenu &&
          (nuvemPagoMenu.options ? (
            <MenuButton.Accordion
              contentid={`${nuvemPagoMenu.key}-content`}
              menuButton={{
                id: `${nuvemPagoMenu.key}-control`,
                label: nuvemPagoMenu.label,
                startIcon: MoneyIcon,
                'aria-controls': `${nuvemPagoMenu.key}-content`,
                onClick: handleNuvemPagoClick(nuvemPagoMenu, true),
              }}
              as={getButtonElement(nuvemPagoMenu)}
              to={nuvemPagoMenu.goTo ?? ''}
              href={getOptionHref(nuvemPagoMenu)}
              active={nuvemPagoMenuIsActive}
              open={nuvemPagoMenuIsActive}
            >
              {Object.values(nuvemPagoMenu.options).map((option) => (
                <MenuButton
                  key={option.key}
                  as={getButtonElement(option)}
                  to={option.goTo ?? ''}
                  href={getOptionHref(option)}
                  active={isActive(option.goTo ?? '')}
                  startIcon={option.icon ? getIcon(option.icon) : undefined}
                  label={option.label}
                  onClick={handleNuvemPagoClick(option)}
                >
                  {!!option.tag && <Tag appearance="primary">{option.tag}</Tag>}
                </MenuButton>
              ))}
            </MenuButton.Accordion>
          ) : (
            <MenuButton
              key={nuvemPagoMenu.key}
              as={getButtonElement(nuvemPagoMenu)}
              to={nuvemPagoMenu.goTo ?? ''}
              href={getOptionHref(nuvemPagoMenu)}
              active={nuvemPagoMenuIsActive}
              startIcon={MoneyIcon}
              label={nuvemPagoMenu.label}
              onClick={handleNuvemPagoClick(nuvemPagoMenu)}
            />
          ))}
      </>
    </ShowByRole>
  );
}

export default NuvemPagoMenu;
